import { range } from '@/utils'
import type { Bar } from './scrubberBar'

// Room between one label's end and the next label's start on the same row.
const LABEL_GAP = 12
// Labels that would run into each other climb to a higher row, up to this many.
export const MOST_ROWS = 3

export interface AccountMarkSource {
  id: string
  label: string
}

export type AccountOf = (statementId: string) => AccountMarkSource | null
export type TextWidth = (text: string) => number

export interface RulerMark {
  x: number
  // Null only when every row is taken: the tick stands alone.
  label: string | null
  // 0 is the row nearest the bar.
  row: number
}

export interface Ruler {
  marks: RulerMark[]
  rows: number
}

const EMPTY_RULER: Ruler = { marks: [], rows: 1 }

// A tick where the bar moves on to the next account, named with its number. Short accounts
// sit closer together than their names are wide, so a name that would overlap the last one
// goes up a row, and its tick climbs with it. Statements filed under no account, and piles of
// unfiled pages, make no mark.
export const rulerMarks = (
  bar: Bar,
  accountOf: AccountOf,
  textWidth: TextWidth,
): Ruler => {
  const marks: RulerMark[] = []
  const rowEndsAt: number[] = []
  let previousId: string | null = null

  for (const sliver of bar.slivers) {
    const account = accountOf(sliver.statementId)
    if (!account || account.id === previousId) continue
    previousId = account.id

    const mark: RulerMark = { x: sliver.left, label: null, row: 0 }
    const fits = (row: number) =>
      rowEndsAt[row] === undefined || mark.x >= rowEndsAt[row] + LABEL_GAP
    const row = range(MOST_ROWS).find(fits)
    if (row !== undefined) {
      mark.label = account.label
      mark.row = row
      rowEndsAt[row] = mark.x + textWidth(account.label)
    }
    marks.push(mark)
  }
  return marks.length === 0
    ? EMPTY_RULER
    : { marks, rows: Math.max(rowEndsAt.length, 1) }
}
